import {IServiceFactory} from "../../service-factory.interface";
import {computed, IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {
    IDotRezBundleAvailability
} from "../../dot-rez-api/data-contracts/booking/bundles-availability/bundles-availability.data-contracts";
import {NullableString, NullableUndefinedString} from "../../../types/nullable-types";
import {MaturePassengerModel} from "./passenger/mature-passenger.model";
import {BookingContactModel} from "./contacts/booking-contact.model";
import {PassengersListModel} from "./passenger/passengers-list.model";
import {Check} from "../../../types/type-checking";
import {Price} from "../../currency/price";
import {ISsrType} from "../../ssr-types/ssr-types.service.interface";
import {
    IDotRezBookingSessionData,
    IDotRezPartialBookingSessionData
} from "../../dot-rez-api/data-contracts/booking/dot-rez-booking-session-data.interface";
import {
    IDotRezBookingComment,
    IDotRezBookingData,
    IDotRezContact
} from "../../dot-rez-api/data-contracts/booking/booking-state/booking-state.data-contracts";
import {IDotRezBookingSession} from "../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {BookingSessionStorageKeys} from "./storage/booking-session-storage-keys";
import {IBookingViewModel, IFareToSell} from "./booking-view-model.interface";
import {
    IDotRezSsrAvailability
} from "../../dot-rez-api/data-contracts/booking/ssrs-availability/ssr-availability.data-contracts";
import {PassengerSegmentModel} from "./passenger-segment/passenger-segment.model";
import {SegmentModel} from "./segment/segment.model";
import {JourneyModel} from "./journey/journey.model";
import {IDotRezCheckInRequirements} from "../../dot-rez-api/data-contracts/booking/check-in/check-in.data-contracts";
import {
    IDotRezCheckInSegmentPassengersRequest
} from "../../dot-rez-api/data-contracts/requests/booking/check-in-segment-passengers.request";
import {ISegmentViewModel} from "./segment/segment-view-model.interface";
import {IBookingStrategyToBookingAdapter} from "../booking-strategies/booking-strategy-to-booking.adapter.interface";
import {
    IPassengerSegmentBoardingPassViewModel
} from "../boarding-pass/passenger-segment-boarding-pass-view-model.interface";
import {PaymentHandlerModel} from "./payment/payment-handler.model";
import {BoardingPassesReader} from "../boarding-pass/boarding-passes-reader";
import {BookingMutationsManagerModel} from "./mutation-actions/booking-mutations-manager.model";
import {SellSsrsMutation} from "./mutation-actions/sell-ssrs/sell-ssrs.mutation";
import {PromoCodeModel} from "./promotions/promo-code/promo-code.model";
import {BookingSeatsMapsEditorModel} from "./seat-maps/booking-seats-maps-editor.model";
import {IPersonViewModel} from "../../user/models/person/person-view-model.interface";
import {BookingBlueBenefitsModel} from "./blue-benefits/booking-blue-benefits.model";
import {IBookingAnalyticsConfiguration} from "./analytics/booking-analytics-configuration.inteface";
import {ManageMyBookingModel} from "./manage-my-booking/manage-my-booking.model";
import {BookingSnapshotModel} from "./snapshots/booking/booking-snapshot.model";
import {Lazy} from "../../../utils/lazy";
import {
    IFlightSearchControllerViewModel
} from "../../flight-search/flight-search-controller/flight-search-controller-view-model.interface";
import {BookingBaseModel} from "./base-models/booking/booking-base.model";
import {BookingCommentTypeEnum} from "../../dot-rez-api/data-contracts/enums/booking-comment-type.enum";
import {FlexConsumptionHandler} from "./manage-my-booking/flex-consumption-handler";
import {BookingShoppingCartModel} from "./shopping-cart/booking/booking-shopping-cart.model";
import {ShoppingCartModeEnum} from "../booking-strategies/booking-strategy.interface";
import {PassengerFeeModel} from "./fees/passenger/passenger-fee.model";
import {BookingInsuranceModel} from "./insurance/booking-insurance.model";
import {AirlineCompanyName} from "../../../global-constants";
import {IInvoiceModel} from "./invoice/invoice.model.interface";
import {AeroitaliaInvoiceModel} from "./invoice/aeroitalia/aeroitalia.invoice.model";
import {
    IBookingSsrsAggregatorOptions,
    IBookingSsrsAggregatorViewModel
} from "./ssrs/aggregator/booking-ssrs-aggregator-view-model.interface";
import {BookingSsrsAggregator} from "./ssrs/aggregator/booking-ssrs-aggregator";
import {SameForAllFlightsStorage} from "./storage/same-for-all-flights-storage";
import {IWizard} from "../../../models/wizard/wizard.interface";
import {composeDesignatorUniqueKey} from "./designator/flight-designator.model";
import {ValidationResultEnum} from "../../../types/validation-result.enum";
import {BookingAnalyticsHandlerModel} from "./analytics/booking-analytics-handler.model";
import {BookingErrorHandlingModel} from "./booking-error-handling.model";
import {BookingFlowTypeEnum} from "../booking-strategies/booking-flow-type.enum";
import {PassengersTypesChangeMutation} from "./mutation-actions/passengers/passengers-types-change.mutation";
import {BookingPsoModel} from "./pso/booking-pso.model";
import {DotRezDomainCodes} from "../../dot-rez-api/data-contracts/enums/domain-codes.enum";
import {BookingSessionStorage} from "./storage/booking-session-storage";
import {IPassengerSegmentViewModel} from "./passenger-segment/passenger-segment-view-model.interface";
import {Debounce} from "../../../utils/debounce";
import {TimeSpan} from "../../../types/time-span";
import {BookingStatusDeeplinkQueryParamsEnum} from "../../deep-links/booking-status-deeplink-query-params.enum";
import {IBundleSsrUpgradePair} from "./bundle/bundle-ssr-upgrade.model.interface";


export class BookingModel extends BookingBaseModel implements IBookingViewModel {
    constructor(public readonly services: IServiceFactory,
                public readonly session: IDotRezBookingSession,
                bookingSessionData: IDotRezBookingSessionData,
                public readonly bookingStrategyAdapter: IBookingStrategyToBookingAdapter) {
        super();
        this.storage = bookingStrategyAdapter.createBookingStorage(() => this.session.token);
        this.sameForAllFlightsStorage = new SameForAllFlightsStorage(this);
        this.bookingTimestamp = this._createTimeStamp();
        this._bookingSessionData = bookingSessionData;
        this.mutationsManager = new BookingMutationsManagerModel(this);
        this.errorHandling = new BookingErrorHandlingModel(this);

        this.pso = new BookingPsoModel(this); //Don't move it down. It should be before the ones below.

        const storedDepartureJourneyKey = this.storage.getItem(BookingSessionStorageKeys.departureJourneyKey);
        if(!storedDepartureJourneyKey && this.bookingData.journeys[0]?.journeyKey) {
            this.storage.setItem(BookingSessionStorageKeys.departureJourneyKey, this.bookingData.journeys[0].journeyKey);
        }

        const storedReturnJourneyKey = this.storage.getItem(BookingSessionStorageKeys.returnJourneyKey);
        if(!storedReturnJourneyKey && this.bookingData.journeys[1]?.journeyKey) {
            this.storage.setItem(BookingSessionStorageKeys.returnJourneyKey, this.bookingData.journeys[1].journeyKey);
        }



        this._createJourneysModels();

        this.paymentHandler = new PaymentHandlerModel(this);
        this.analyticsHandler = new BookingAnalyticsHandlerModel(this);
        this.blueBenefits = new BookingBlueBenefitsModel(this);

        //This will prevent the user to see the balance due jumping up and down when multiple mutations are running and updates the balance due multiple times
        this._reactions.push(reaction(() => this.mutationsManager.isBookingTotalUpdateInProgress,
                (isTotalUpdateInProgress) => {
                    if(isTotalUpdateInProgress) {
                        this._freezeBalanceDueUpdates();
                    } else {
                        this._unFreezeBalanceDueUpdates();
                    }
        }));


        makeObservable<this, '_bookingSessionData'
                             | '_departureJourney'
                             | '_returnJourney'
                             | '_frozenBalanceDue'
                             | '_bookingContactModel'
                             | '_isBookingDiscardChangesInProgress'
                             | '_travelConditionsAccepted'
                             | '_travelConditionsAcceptanceError'
                             >(this, {
            _bookingSessionData: observable,
            _departureJourney: observable.ref,
            _returnJourney: observable.ref,
            allAvailableSsrsCodes: computed,
            filteredJourneys: computed,
            unfilteredJourneys: computed,
            _frozenBalanceDue: observable.ref,
            _bookingContactModel: observable.ref,
            _isBookingDiscardChangesInProgress: observable.ref,
            _travelConditionsAccepted: observable.ref,
            _travelConditionsAcceptanceError: observable.ref,
            bagsSsrsAggregator: computed
        });
    }

    readonly sameForAllFlightsStorage: SameForAllFlightsStorage;
    readonly storage: BookingSessionStorage;
    readonly mutationsManager: BookingMutationsManagerModel;
    private _bookingSessionData: IDotRezBookingSessionData;
    readonly paymentHandler: PaymentHandlerModel;
    readonly analyticsHandler: BookingAnalyticsHandlerModel;
    readonly errorHandling: BookingErrorHandlingModel;
    readonly pso: BookingPsoModel;
    private readonly _invoice: Lazy<IInvoiceModel> = new Lazy<IInvoiceModel>(() => {
        return new AeroitaliaInvoiceModel(this);
        //return new BlueAirInvoiceModel(this);
    });

    get invoice(): IInvoiceModel {
        return this._invoice.value;
    }
    readonly blueBenefits: BookingBlueBenefitsModel;
    private readonly _shoppingCart: Lazy<BookingShoppingCartModel> = new Lazy<BookingShoppingCartModel>(() => {
        return new BookingShoppingCartModel(this);
    })


    get shoppingCart(): BookingShoppingCartModel {
        return this._shoppingCart.value;
    }

    private _insurance: Lazy<BookingInsuranceModel> = new Lazy<BookingInsuranceModel>(() => {
        return new BookingInsuranceModel(this);
    })
    get insurance(): BookingInsuranceModel {
        return this._insurance.value;
    }


    readonly bookingTimestamp: number;
    private _reactions: IReactionDisposer[] = [];

    private _createTimeStamp(): number {
        const storedTimestamp = this.storage.getItem(BookingSessionStorageKeys.bookingTimestamp);
        if(storedTimestamp) {
            return parseInt(storedTimestamp);
        } else {
            const timeStamp = this.services.time.currentDate.getTime();
            this.storage.setItem(BookingSessionStorageKeys.bookingTimestamp, timeStamp.toString());
            return timeStamp;
        }
    }

    private readonly _seatsMapsEditors: Lazy<BookingSeatsMapsEditorModel> = new Lazy<BookingSeatsMapsEditorModel>(() => new BookingSeatsMapsEditorModel(this))
    get seatsMapsEditors(): BookingSeatsMapsEditorModel {
        return this._seatsMapsEditors.value;
    }

    private readonly _manageMyBooking: Lazy<ManageMyBookingModel> = new Lazy<ManageMyBookingModel>(() => new ManageMyBookingModel(this));
    get manageMyBooking(): ManageMyBookingModel {
        return this._manageMyBooking.value;
    }


 
    get token(): string {
        return this.session.token;
    }

    get bookingSessionData(): IDotRezBookingSessionData {
        return this._bookingSessionData;
    }

    get hasPromotionApplied(): boolean {
        return this.promoCode.isApplied || this.getAllFilteredPassengersSegments().some(p => p.shoppingCart.hasPromotionApplied);
    }


    get bookingData(): IDotRezBookingData {
        return this.bookingSessionData.bookingData;
    }



    get bundlesAvailability(): IDotRezBundleAvailability[] {
        return this.bookingSessionData.bundlesAvailability;
    }

    get ssrsAvailability(): IDotRezSsrAvailability {
        return this.bookingSessionData.ssrsAvailability;
    }

    get checkInRequirements(): IDotRezCheckInRequirements | null | undefined {
        return this.bookingSessionData.checkInRequirements;
    }

    get shouldSyncContactWithPrimaryPassenger(): boolean {
        return this.bookingStrategyAdapter.shouldSyncContactWithPrimaryPassenger;
    }
    private _bookingContactModel: Lazy<BookingContactModel> = new Lazy<BookingContactModel>(() => new BookingContactModel(this));
    get contact(): BookingContactModel {
        return this._bookingContactModel.value;
    }


    get bookingKey(): NullableString {
        return this.bookingData.bookingKey;
    }
    get recordLocator(): NullableString {
        return this.bookingData.recordLocator;
    }

    get isCommitted(): boolean {
        return Boolean(this.recordLocator);
    }


    private _passengers: PassengersListModel | null = null;
    get passengers(): PassengersListModel {
        if(this.bookingData.passengers.length === 0) {
            return new PassengersListModel();
        }

        if(!this._passengers) {
            this._passengers = new PassengersListModel(this
                .bookingSessionData
                .bookingData
                .passengers.map((item, index) => new MaturePassengerModel(item.key, index, this)));

            if(Check.isNullOrUndefined(this.storage.getItem(BookingSessionStorageKeys.primaryContact))) {
                const defaultContactPassenger =  this._passengers.find(p => p.passengerType.canBeMadePrimaryContact);
                if(defaultContactPassenger) {
                    defaultContactPassenger.isPrimaryContact = true;
                }
            }
        }
        return this._passengers;
    }

    private _journeyKeyExistsInBooking(journeyKey: string): boolean {
        return Boolean(this.bookingData.journeys.find(j => j.journeyKey === journeyKey));
    }

    private _tryFindJourneyKey(index: number, storageKey: BookingSessionStorageKeys.departureJourneyKey | BookingSessionStorageKeys.returnJourneyKey): NullableUndefinedString {
        let journeyKey = this.storage.getItem(storageKey);
        if(!journeyKey || !this._journeyKeyExistsInBooking(journeyKey)) {
            return this.bookingData.journeys[index]?.journeyKey;
        }

        return journeyKey;
    }

    private _createDepartureJourneyModel(): JourneyModel | null {
        let journeyKey = this._tryFindJourneyKey(0, BookingSessionStorageKeys.departureJourneyKey);
        if(journeyKey) {
            return new JourneyModel(journeyKey, this, this.initialBookingSnapshot.departureJourney);
        } else {
            return null;
        }
    }

    private _createReturnJourneyModel(): JourneyModel | null {
        let journeyKey = this._tryFindJourneyKey(1, BookingSessionStorageKeys.returnJourneyKey);

        if(journeyKey) {
            return new JourneyModel(journeyKey, this, this.initialBookingSnapshot.returnJourney);
        } else {
            return null;
        }
    }


    private _createJourneysModels(): void {
        runInAction(() => {
            this._departureJourney = this._createDepartureJourneyModel();
            this._returnJourney = this._createReturnJourneyModel();
        });
    }

    private _departureJourney: JourneyModel | null = null;
    get departureJourney(): JourneyModel | null {

        if(this._departureJourney && !this.bookingStrategyAdapter.shouldFilterOutJourney(this._departureJourney)) {
            return this._departureJourney;
        }

        if(this._returnJourney && !this.bookingStrategyAdapter.shouldFilterOutJourney(this._returnJourney)) {
            return this._returnJourney;
        }

        return null;
    }

    private _returnJourney: JourneyModel | null = null;
    get returnJourney(): JourneyModel | null {
        if(this.departureJourney?.journeyKey === this._returnJourney?.journeyKey) {
            return null;
        }

        if(this._returnJourney && !this.bookingStrategyAdapter.shouldFilterOutJourney(this._returnJourney)) {
            return this._returnJourney;
        }

        return null;
    }

    get unfilteredJourneys(): JourneyModel[] {

        let result: JourneyModel[] = [];

        if(this._departureJourney) {
            result.push(this._departureJourney);
        }

        if(this._returnJourney) {
            result.push(this._returnJourney);
        }

        return result;
    }

    get filteredJourneys(): JourneyModel[] {
        let journeys: JourneyModel[] = [];
        if(this.departureJourney) {
            journeys.push(this.departureJourney);
        }

        if(this.returnJourney) {
            journeys.push(this.returnJourney);
        }

        return journeys;
    }


    get isOneWayTrip(): boolean {
        return !Boolean(this.returnJourney);
    }

    get minSeatsFee(): Price {
        return Price.min(this.filteredJourneys.map(journey => journey.minSeatsFee),
                         this.createPrice(0));
    }

    get allAvailableSsrsCodes(): Record<string, string> {
        const ssrsCodes: Record<string, string> = {};
        this.ssrsAvailability.segmentSsrs.forEach(segmentSsrs => {
            segmentSsrs.ssrs.forEach(ssr => ssrsCodes[ssr.ssrCode] = ssr.ssrCode);
        });
        return ssrsCodes;
    }

    hasAvailabilityForSSR(ssrType: ISsrType): boolean {
        for(let j of this.filteredJourneys) {
            for(let s of j.segments) {
                if(s.ssrsAvailability.getSsrAvailability(ssrType).availableQuantity > 0) {
                    return true;
                }
            }
        }
        return false;
    }

    hasPurchasesForSSR(ssrType: ISsrType): boolean {
        return this.getAllFilteredPassengersSegments().some(p => p.getSsr(ssrType).currentQuantity > 0);
    }

    private async _onAfterSellDepartureJourney(fareToSell: IFareToSell, bookingSessionData: IDotRezPartialBookingSessionData): Promise<void>{
        runInAction(() => {
            this.unfilteredJourneys.forEach(j => j.markQuantityAsUnsetForAllSoldSsrs());
            this.storage.setItem(BookingSessionStorageKeys.departureJourneyKey, fareToSell.journeyKey);
            this._passengers = null;
            this.updateBookingSessionData(bookingSessionData);
            this._createJourneysModels();
            // Razvan - call for view_item (I have also access to bundlelist)
            if(this.departureJourney){
                this.services.analytics.flightSelectionEvents.logSelectItemForDeparture(this.departureJourney);
            }
        });
    }


    async sellDepartureJourney(fareToSell: IFareToSell): Promise<void> {
        await this.bookingStrategyAdapter.sellDepartureJourney(fareToSell, (bookingSessionData) => this._onAfterSellDepartureJourney(fareToSell, bookingSessionData));
    }

    async sellDepartureJourneyBundle(bundleCode: string, inBundleSsrsUpgrades: IBundleSsrUpgradePair[] = []): Promise<void> {
        await this.bookingStrategyAdapter.sellDepartureJourneyBundle(bundleCode, {
            ssrsUpgradePairs: inBundleSsrsUpgrades
        });
    }

    async upgradeDepartureJourneyBundleSsr(ssrsUpgradePairs: IBundleSsrUpgradePair[]): Promise<void> {
        if(!this.departureJourney?.currentBundleCode) {
            throw new Error('No departure journey to upgrade the SSR for');
        }
        await this.sellDepartureJourneyBundle(this.departureJourney?.currentBundleCode, ssrsUpgradePairs);
    }

    private async _onAfterSellReturnJourney(fareToSell: IFareToSell, bookingSessionData: IDotRezPartialBookingSessionData): Promise<void> {
        runInAction(() => {
            this.unfilteredJourneys.forEach(j => j.markQuantityAsUnsetForAllSoldSsrs());
            this.storage.setItem(BookingSessionStorageKeys.returnJourneyKey, fareToSell.journeyKey);
            this.updateBookingSessionData(bookingSessionData);
            this._createJourneysModels()

            if(this.returnJourney) {
                this.services.analytics.flightSelectionEvents.logSelectItemForReturn(this.returnJourney!);
            }
        });
    }

    async sellReturnJourney(fareToSell: IFareToSell): Promise<void> {
        await this.bookingStrategyAdapter.sellReturnJourney(fareToSell, bookingSessionData => this._onAfterSellReturnJourney(fareToSell, bookingSessionData));
    }

    async sellReturnJourneyBundle(bundleCode: string): Promise<void> {
        await this.bookingStrategyAdapter.sellReturnJourneyBundle(bundleCode);
    }


    get allJourneysAreCanceled(): boolean {
        return this.unfilteredJourneys.all(j => j.isCanceled);
    }

    get initialBookingSnapshot(): BookingSnapshotModel {
        return this.bookingStrategyAdapter.initialBookingSnapshot;
    }

    getAllFilteredSegments(): SegmentModel[] {
        return this.filteredJourneys.selectMany(j => j.segments);
    }

    getAllUnFilteredSegments(): SegmentModel[] {
        return this.unfilteredJourneys.selectMany(j => j.segments);
    }

    getAllFilteredPassengersSegments(): PassengerSegmentModel[] {
        return this.getAllFilteredSegments().selectMany(s => s.passengers);
    }

    getAllUnFilteredPassengersSegments(): IPassengerSegmentViewModel[] {
        return this.getAllUnFilteredSegments().selectMany(s => s.passengers);
    }

    getSSR(ssrCode: string): ISsrType {
        return this.services.ssrTypes.getSsrType(ssrCode);
    }

    private _sellSsrDebounce: Debounce = new Debounce(TimeSpan.fromMilliseconds(500));
    async sellSsrs(options?: {skipDebounce: boolean}): Promise<void> {

        options = {
            skipDebounce: false,
            ...options
        };


        if(options.skipDebounce) {
            this.mutationsManager.startMutation(new SellSsrsMutation(this));
        } else {
            this._sellSsrDebounce.execute(() => {
                this.mutationsManager.startMutation(new SellSsrsMutation(this));
            });
        }

    }

    updateBookingSessionData(bookingSessionData: IDotRezPartialBookingSessionData): void {
        if(Check.isEmpty(bookingSessionData)) {
            return;
        }

        runInAction(() => {
            this._bookingSessionData = {
                ...this.bookingSessionData,
                ...bookingSessionData
            }
        });
    }

    tryChangePassengersTypes(): void {
        this.mutationsManager.startMutation(new PassengersTypesChangeMutation(this))
    }

    async reset(): Promise<void> {
        await this.session.bookingReset();
    }


    async addContact(contactData: IDotRezContact): Promise<void> {
        await this.session.addBookingContact(contactData);
    }

    async updateContact(contactData: IDotRezContact): Promise<void> {
        await this.session.updateBookingContact(contactData);
    }


    getSoldSsrsCount(ssrType: ISsrType): number {
        return this.getAllFilteredPassengersSegments().sum(ps => ps.getSsr(ssrType).currentQuantity);
    }

    getSoldMeals(segment?: ISegmentViewModel): Record<string, {ssrType: ISsrType, count: number}> {
        const soldMeals: Record<string, {ssrType: ISsrType, count: number}> = {};

        let passengerSegments = this.getAllFilteredPassengersSegments();

        if(segment){
            passengerSegments = passengerSegments.filter(ps => ps.segmentKey === segment.segmentKey);
        }

        for(let mealSsrType of this.services.ssrTypes.getMealsSsrTypes()) {
            for(let passengerSegment of passengerSegments) {
                const ssr = passengerSegment.getSsr(mealSsrType);
                if(ssr.newQuantity > 0) {
                    if(!soldMeals[mealSsrType.ssrCode]) {
                        soldMeals[mealSsrType.ssrCode] = {
                            ssrType: mealSsrType,
                            count: 0
                        };
                    }
    
                    soldMeals[mealSsrType.ssrCode].count += ssr.newQuantity;
                }
            }
        }

        return soldMeals;
    }

    dispose() {
        this._sellSsrDebounce.dispose();
        this.paymentHandler.dispose();
        this.analyticsHandler.dispose();
        this.contact.dispose();
        this.passengers.forEach(p => p.dispose());
        this.insurance.dispose();
        this.session.dispose();
        this.storage.dispose();
        if(this._manageMyBooking.isInitialized) {
            this._manageMyBooking.value.dispose();
        }

        this._reactions.forEach(r => r());
        this._reactions = [];
    }

    async executeCheckIn(options: {saveTravelDocuments: boolean}): Promise<ValidationResultEnum> {
        return await this.services.loadingIndicator.execute({
            action: async () => {
                try {
                    if(options.saveTravelDocuments) {
                        await this.passengers.saveTravelDocuments();
                    }

                    await this._executeOnlineCheckInForEligiblePassengers();

                    return ValidationResultEnum.Success;
                } catch (err) {
                    this.services.logger.error('Failed to execute checkin', err);
                    await this.services.alert.showErrorAsync(this.services.language.translate('There was an unexpected error trying to execute the check-in operation.'));
                    return ValidationResultEnum.Failure;

                }

            }
        });
    }

    private async _executeOnlineCheckInForEligiblePassengers(): Promise<void> {

        let request: IDotRezCheckInSegmentPassengersRequest[] = [];
        this.filteredJourneys.filter(j => j.isOpenForCheckIn)
                                  .forEach(journey => {
                                      journey.segments.forEach(segment => {
                                          const eligiblePassengers = segment.getEligiblePassengersForOnlineCheckIn()
                                              .filter(passengerSegment => !passengerSegment.isCheckedIn)
                                              .map(passengerSegment => {
                                                  return {passengerKey: passengerSegment.passenger.passengerKey}
                                              });

                                          if (eligiblePassengers.length > 0) {
                                              request.push({
                                                  segmentKey: segment.segmentKey,
                                                  shouldAssignSeats: segment.passengers.some(p => Check.isNullOrUndefined(p.assignedSeat)),
                                                  request: {
                                                      passengers: eligiblePassengers
                                                  }
                                              });
                                          }
                                      });
                                  });

        if(request.length === 0) {
            await this.commitBookingAndUpdateBookingState({notifyContacts: false});
            return;
        }

        await this.session.checkInPassengers(request);

        const bookingStateData = await this.session.bookingStateQueryBuilder().useBookingData().useCheckInRequirements().getBookingState();

        this.updateBookingSessionData(bookingStateData);
    }


    async generateBoardingPasses(): Promise<IPassengerSegmentBoardingPassViewModel[]> {
        const boardingPassesReader = new BoardingPassesReader(this.bookingData, this.session, this.services);
        const boardingPasses = await boardingPassesReader.getBoardingPasses();
        return this.services.bookingHistory.saveBoardingPasses(boardingPasses);
    }

    saveToMyTrips() {
        if(!this.recordLocator) {
            return;
        }

        if(this.services.layout.shouldUseWebappLayout) {
            return;
        }

        try {
            this.services.bookingHistory.saveToMyTrips(this);
            this.generateBoardingPasses().catch(err => {
                this.services.logger.error('Failed to generate boarding passes after saveToMyTrips', err);
            });
        } catch (err) {
            this.services.logger.error(`Failed to save booking ${this.recordLocator} in local storage`, err);
        }
    }


    async getBoardingPassesToShow(): Promise<IPassengerSegmentBoardingPassViewModel[]> {
        let segments = this.getAllUnFilteredSegments().map(segment => segment.designator.uniqueKey);
        let bPasses = await this.generateBoardingPasses();
        bPasses = bPasses.filter(bp => segments.includes(composeDesignatorUniqueKey(bp.designator.origin.code, bp.designator.destination.code)));
        return bPasses;
    }


    async showBoardingPasses(): Promise<void> {
        const boardingPasses = await this.services.loadingIndicator.execute({
            action: async () => {
                this.services.bookingHistory.saveToMyTrips(this);
                return await this.getBoardingPassesToShow();
            }
        });

        await this.services.dialogFactory.showBoardingPasses({
            bookingKey: this.bookingData.bookingKey!,
            boardingPasses: boardingPasses
        });
    }


    private _frozenBalanceDue: Price | null = null;

    private _freezeBalanceDueUpdates(): void {
        runInAction(() => {
            this._frozenBalanceDue = this.balanceDue;
        })
    }

    private _unFreezeBalanceDueUpdates(): void {
        runInAction(() => {
            this._frozenBalanceDue = null;
        })
    }

    get balanceDue(): Price {
        if(this._frozenBalanceDue) {
            return this._frozenBalanceDue;
        }

        return this.createPrice(this.bookingData.breakdown.authorizedBalanceDue - this.paymentHandler.totalAuxiliaryPaymentsAmount);
    }

    get hasPurchasesOnCurrentSession(): boolean {
        return this.balanceDue.amount > 0
                || this.shoppingCart.hasPurchaseOnCurrentSession
                || this.passengers.some(p => p.hasChangesOnCurrentSession() || Boolean(p.infant?.hasChangesOnCurrentSession()))
                || this.contact.hasChangesOnCurrentSession();
    }

    get hasChangedSegments(): boolean {
        return this.unfilteredJourneys.some(j => j.hasChangedSegments);
    }

    get isDomesticFlight(): boolean {
        const allSegments = this.getAllUnFilteredSegments();
        if(allSegments.length === 0) {
            return false;
        }

        return allSegments.all(seg => seg.isDomesticFlight());

    }


    private _promoCode: PromoCodeModel | null = null;
    get promoCode(): PromoCodeModel {
        if(!this._promoCode) {
            this._promoCode = new PromoCodeModel(this);
        }
        return this._promoCode;
    }

    get allowPromoCode(): boolean {
        return this.bookingStrategyAdapter.allowPromoCode;
    }

    private _getBlueBenefitsComment(): IDotRezBookingComment[] {
        const blueBenefitsComment = this.blueBenefits.createBookingCommentText();
        if(blueBenefitsComment) {
            return [this._createComment(blueBenefitsComment)];
        }

        return [];
    }

    get allowHold(): boolean {
        return this.bookingStrategyAdapter.allowHold;
    }

    async commitBooking(options: {notifyContacts: boolean}): Promise<void> {
        await this.session.commitBooking({
            notifyContacts: options.notifyContacts,
            channel: await this.services.booking.getCommitBookingChannel(),
            comments: this._getBlueBenefitsComment()
        });
    }

    async putInHold(): Promise<Date> {
        const time = this.services.time;
        const holdExpiration = time.addDays(time.currentDate, 1);
        await this.session.commitBooking({
            notifyContacts: false,
            channel: await this.services.booking.getCommitBookingChannel(),
            holdExpiration: time.formatUtcZuluTime(holdExpiration)
        });
        return holdExpiration;
    }

    async commitBookingAndUpdateBookingState(options: {notifyContacts: boolean}): Promise<void> {

        await this.commitBooking(options);

        const bookingSessionData =  await this.session.bookingStateQueryBuilder().useBookingData().getBookingState();

        this.updateBookingSessionData(bookingSessionData);

    }

    shouldShowSsrOnCurrentFlow(ssrType: ISsrType): boolean {
        return this.bookingStrategyAdapter.canShowSsr(ssrType);
    }

    getAvailableCompanions(): IPersonViewModel[] {
        let usedCompanions = this.passengers.filter(p => p.customerNumber).map(p => p.customerNumber);
        usedCompanions = [
            ...usedCompanions,
            ...this.passengers.filter(p => p.infant?.customerNumber)
                              .map(p => p.infant?.customerNumber!)
        ]
        return this.services.user.profile.getCompanionsForBooking().filter(companion => !usedCompanions.includes(companion.customerNumber));
    }



    get analyticsConfiguration(): IBookingAnalyticsConfiguration {
        return {
            ...this.bookingStrategyAdapter.analyticsConfiguration,
            itemBrand: AirlineCompanyName,
            itemVariant: this.isOneWayTrip ? "OneWay" : "RoundTrip"
        };
    }

    async upgradeToAuthorizedUser(userName: string, password: string, domain: DotRezDomainCodes): Promise<void> {
        await this.session.upgradeToAuthorizedUser(userName, password, domain);
    }

    get flightSearchController(): IFlightSearchControllerViewModel {
        return this.bookingStrategyAdapter.flightSearchController;
    }

    private async _moveBetweenQueues(fromQueue: string, toQueue: string): Promise<boolean> {
        if(!this.isInQueue(fromQueue)) {
            return false;
        }
        try {
            const authorizedBy = await this.services.booking.getCommitBookingChannel();
            await this.session.putBookingInQueue({
                queueCode: toQueue,
                authorizedBy: authorizedBy,
                notes: `Moved from ${fromQueue}`
            });

            try {
                await this.session.removeBookingFromQueue({
                    queueCode: fromQueue,
                    authorizedBy: authorizedBy,
                    notes: `Moved to ${toQueue}`
                });
            } catch (errRemove) {
                this.services.logger.error(`Failed to remove booking ${this.recordLocator} from queue ${fromQueue}`, errRemove);
            }

            return true;
        } catch (err) {
            this.services.logger.error(`Failed to move booking ${this.recordLocator} from queue ${fromQueue} to queue ${toQueue}`, err);
            return false;
        }
    }

    private _createComment(text: string): IDotRezBookingComment {
        return {
            type: BookingCommentTypeEnum.Default,
            text: text
        };
    }

    async moveBetweenDisruptionQueues(): Promise<void> {

        if(!this.initialBookingSnapshot.canMoveDisruptedFlights) {
            return;
        }

        if(!this.hasChangedSegments) {
            return;
        }

        const results: boolean[] = [];
        results.push(await this._moveBetweenQueues('CXL', 'CXLSM'))
        results.push(await this._moveBetweenQueues('REAO3H', 'RMO3SM'));
        results.push(await this._moveBetweenQueues('SCHO3H', 'SCO3SM'))


        if(results.some(moved => moved)) {

            const date = this.services.time.formatYYYY_MM_DD(this.services.time.currentDate);
            const time = this.services.time.formatHHmm(this.services.time.currentDate);
            const email = this.contact.fields.emailAddress.value;
            try {
                await this.session.commitBooking({
                    comments: [
                        this._createComment(`Move; ${email}; ${date}; ${time}; mobile application`)
                    ],
                    notifyContacts: false,
                    channel: await this.services.booking.getCommitBookingChannel()
                });
            } catch (err) {
                this.services.logger.error('Failed to add move comment', err);
            }

        }
    }

    async removeFromDisruptionQueues(): Promise<void> {

        if(!this.initialBookingSnapshot.canMoveDisruptedFlights) {
            return;
        }

        if(!this.hasChangedSegments) {
            return;
        }

        const queues = ["CXL", "SCHO3H", "SCHMIS", "REAO3H", "REACXL", "REAU3H", "RACXL"];
        const authorizedBy = await this.services.booking.getCommitBookingChannel();
        for(let queue of queues) {
            if(this.isInQueue(queue)) {
                try {
                    await this.session.removeBookingFromQueue({
                        queueCode: queue,
                        authorizedBy: authorizedBy,
                        notes: `Removed from queue ${queue}`
                    });
                } catch (err) {
                    this.services.logger.error(`Failed to remove booking ${this.recordLocator} from queue ${queue}`, err);
                }
            }
        }
    }

    async consumeFlex(bookingData: IDotRezBookingData | null | undefined): Promise<IDotRezPartialBookingSessionData> {
        const handler = new FlexConsumptionHandler(this);
        return await handler.consumeFlex(bookingData);
    }

    get passengersNamesChangeBlockingReason(): NullableString {
        return this.initialBookingSnapshot.passengersNamesChangeBlockingReason;
    }

    private _isBookingDiscardChangesInProgress: boolean = false;
    get isBookingDiscardChangesInProgress(): boolean {
        return this._isBookingDiscardChangesInProgress;
    }

    private set isBookingDiscardChangesInProgress(value: boolean) {
        runInAction(() => {
            this._isBookingDiscardChangesInProgress = value;
        })
    }

    async discardChanges(): Promise<void> {
        const email = this.initialBookingSnapshot.getAnyContactEmail();
        const recordLocator = this.initialBookingSnapshot.recordLocator;

        if(!recordLocator || !email) {
            throw new Error('discardChanges is not possible for a booking without e-mail and record locator');
        }

        this.isBookingDiscardChangesInProgress = true;

        try {
            await this.reset();

            const bookingData = await this.session.bringBookingInStateByEmail({
                recordLocator: recordLocator,
                emailAddress: email
            });

            const otherBookingStateInfo = await this.session.bookingStateQueryBuilder(bookingData).useAllAvailabilities().useCheckInRequirements().getBookingState();

            runInAction(() => {
                this.updateBookingSessionData({
                    bookingData: bookingData,
                    ...otherBookingStateInfo
                });

                this.storage.reset();
                this.sameForAllFlightsStorage.reset();

                if(this.bookingData.journeys[0]?.journeyKey) {
                    this.storage.setItem(BookingSessionStorageKeys.departureJourneyKey, this.bookingData.journeys[0].journeyKey);
                }

                if(this.bookingData.journeys[1]?.journeyKey) {
                    this.storage.setItem(BookingSessionStorageKeys.returnJourneyKey, this.bookingData.journeys[1].journeyKey);
                }

                this._passengers?.forEach(p => p.dispose());
                this._passengers = null;
                this.contact.dispose();
                this._bookingContactModel = new Lazy<BookingContactModel>(() => new BookingContactModel(this));

                this._createJourneysModels();

            });

            this.shoppingCart.notifications.clear();
        } finally {
            this.isBookingDiscardChangesInProgress = false;
        }

    }

    get allowedShoppingCartModes(): ShoppingCartModeEnum[] {
        return this.bookingStrategyAdapter.allowedShoppingCartModes;
    }

    get isGDSBooking(): boolean {
        return this.initialBookingSnapshot.isGDSBooking;
    }

    protected _getAllPassengersFees(): PassengerFeeModel[] {
        return this.passengers.selectMany(p => p.fees);
    }

    getBookedDate(): Date {

        if(this.bookingData.info.bookedDate) {
            return this.services.time.parseIsoDate(this.bookingData.info.bookedDate);
        } else {
            return this.services.time.currentDate;
        }
    }





    createSsrsAggregator(options: IBookingSsrsAggregatorOptions): IBookingSsrsAggregatorViewModel {
        return new BookingSsrsAggregator(this, options);
    }


    get bagsSsrsAggregator(): IBookingSsrsAggregatorViewModel {
        return new BookingSsrsAggregator(this, {
            journeysSsrsBuckets: this.filteredJourneys.map(j => j.allBags)
        });
    }

    validateTravelConditionsAcceptance(): ValidationResultEnum {
        if(!this.travelConditionsAccepted) {
            this.travelConditionsAcceptanceError = this.services.language.translate('You must accept the travel conditions');
            this.services.alert.showError(this.travelConditionsAcceptanceError);
            return ValidationResultEnum.Failure;
        }

        return ValidationResultEnum.Success;
    }

    private _travelConditionsAccepted: boolean = false;
    get travelConditionsAccepted(): boolean {
        return this._travelConditionsAccepted;
    }

    set travelConditionsAccepted(value: boolean) {
        runInAction(() => {
            this._travelConditionsAccepted = value
        });

        this.travelConditionsAcceptanceError = null;
    }

    private _travelConditionsAcceptanceError: NullableString = null;
    get travelConditionsAcceptanceError(): NullableString {
        return this._travelConditionsAcceptanceError;
    }

    private set travelConditionsAcceptanceError(value: NullableString) {
        runInAction(() => {
            this._travelConditionsAcceptanceError = value
        });
    }


    get steps(): IWizard {
        return this.bookingStrategyAdapter.steps;
    }

    revertSsrsQuantity(): void {
        for(let ps of this.getAllFilteredPassengersSegments()) {
            ps.revertSsrsQuantity();
        }
    }

    get isNewBooking(): boolean {
        return !Boolean(this.recordLocator);
    }

    get shouldRenderPrintTicketInTheHeader(): boolean {
        return this.bookingStrategyAdapter.bookingFlowType === BookingFlowTypeEnum.ManageMyBooking;
    }

    get dangerousGoodsTermsAndConditionsAccepted(): boolean {
        return this.bookingStrategyAdapter.dangerousGoodsTermsAndConditionsAccepted;
    }

    set dangerousGoodsTermsAndConditionsAccepted(value: boolean) {
        this.bookingStrategyAdapter.dangerousGoodsTermsAndConditionsAccepted = value;
    }

    get allowChangeSearch(): boolean {
        return this.bookingStrategyAdapter.allowChangeSearch;
    }

    get shouldRenderBagsInExtras(): boolean {
        return this.bookingStrategyAdapter.shouldRenderBagsInExtras;
    }

    get shouldValidateBags(): boolean {
        return this.bookingStrategyAdapter.shouldValidateBags;
    }

    get hasPassengersThatBenefitDedicatedFreeSeatsRows(): boolean {
        return this.passengers.some(p => p.passengerType.benefitDedicatedFreeSeats);
    }

    get canBeModified(): boolean {
        return this.bookingStrategyAdapter.canBeModified;
    }
    getCheckInSearchUrl(): string {
        let baseUrl = this.services.configuration.data.b2cBookingEngineUrl;
        if(baseUrl.endsWith('/')) {
            baseUrl = baseUrl.substring(0, baseUrl.length - 1);
        }

        let path = this.services.navigator.routes.checkInSearch.path;
        if(!path.startsWith('/')) {
            path = `/${path}`;
        }

        return `${baseUrl}${path}?${BookingStatusDeeplinkQueryParamsEnum.RecordLocator}=${this.recordLocator ?? ""}&${BookingStatusDeeplinkQueryParamsEnum.Email}=${this.contact.fields.emailAddress.value}`;

    }

    get hasDog(): boolean {
        return this.passengers.countDogs() > 0;
    }
}
